/**
 * API Endpoints Configuration for ArbiWin
 *
 * This module provides centralized endpoint definitions, URL construction,
 * parameter substitution, and validation utilities for all API endpoints
 * used throughout the ArbiWin platform.
 *
 * Features:
 * - Environment-based URL construction
 * - Parameter substitution and validation
 * - Endpoint categorization and organization
 * - URL building utilities
 * - Endpoint testing and validation
 *
 * Following constitution requirements for API integration
 * and surebet data structure handling.
 */

import { getEnvironmentConfig } from '../../../config/environment';
import { convertBookmakerIdsToApiFormat, convertBookmakersStringToApiFormat } from '../../../utils/bookmakerUtils';

/**
 * API Version Configuration
 */
const API_VERSIONS = {
  V1: 'v1',
  V2: 'v2',
  CURRENT: 'v1' // Default version
};

/**
 * Authentication Endpoints
 * Following constitution requirements for JWT token management
 */
const AUTH_ENDPOINTS = {
  // Core authentication
  LOGIN: {
    path: '/api/auth/login',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'User login with email and password',
    requiresAuth: false,
    parameters: {
      required: ['email', 'password'],
      optional: ['rememberMe']
    },
    response: {
      success: ['accessToken', 'refreshToken', 'expiresIn', 'user'],
      error: ['message', 'code']
    }
  },

  REGISTER: {
    path: '/api/auth/register',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'User registration (no email verification required)',
    requiresAuth: false,
    parameters: {
      required: ['email', 'password', 'confirmPassword'],
      optional: ['firstName', 'lastName']
    },
    response: {
      success: ['accessToken', 'refreshToken', 'expiresIn', 'user'],
      error: ['message', 'code', 'validationErrors']
    }
  },

  LOGOUT: {
    path: '/api/auth/logout',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'User logout and token invalidation',
    requiresAuth: true,
    parameters: {
      required: [],
      optional: ['refreshToken']
    }
  },

  REFRESH_TOKEN: {
    path: '/api/auth/refresh',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Refresh JWT access token using refresh token',
    requiresAuth: false,
    parameters: {
      required: ['refreshToken'],
      optional: []
    },
    response: {
      success: ['accessToken', 'refreshToken', 'expiresIn'],
      error: ['message', 'code']
    }
  },

  FORGOT_PASSWORD: {
    path: '/api/auth/forgot-password',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Request password reset email',
    requiresAuth: false,
    parameters: {
      required: ['email'],
      optional: []
    }
  },

  RESET_PASSWORD: {
    path: '/api/auth/reset-password',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Reset password using reset token',
    requiresAuth: false,
    parameters: {
      required: ['token', 'password', 'confirmPassword'],
      optional: []
    }
  },

  VERIFY_EMAIL: {
    path: '/api/auth/verify-email',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Verify email address (optional)',
    requiresAuth: false,
    parameters: {
      required: ['token'],
      optional: []
    }
  }
};

/**
 * User Management Endpoints
 * Role-based access control (User, Admin, Super Admin)
 */
const USER_ENDPOINTS = {
  // User profile management
  GET_PROFILE: {
    path: '/api/users/profile',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get current user profile',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    response: {
      success: ['id', 'email', 'firstName', 'lastName', 'role', 'createdAt', 'subscription']
    }
  },

  UPDATE_PROFILE: {
    path: '/api/users/profile',
    method: 'PUT',
    version: API_VERSIONS.V1,
    description: 'Update user profile information',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['firstName', 'lastName', 'phone', 'preferences']
    }
  },

  GET_PREFERENCES: {
    path: '/api/users/preferences',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get user preferences and settings',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin']
  },

  UPDATE_PREFERENCES: {
    path: '/api/users/preferences',
    method: 'PUT',
    version: API_VERSIONS.V1,
    description: 'Update user preferences',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['language', 'theme', 'notifications', 'autoRefresh']
    }
  },

  DELETE_ACCOUNT: {
    path: '/api/users/delete',
    method: 'DELETE',
    version: API_VERSIONS.V1,
    description: 'Delete user account (irreversible)',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: ['password'],
      optional: ['reason']
    }
  },

  CHANGE_PASSWORD: {
    path: '/api/users/change-password',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Change user password',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: ['currentPassword', 'newPassword', 'confirmPassword'],
      optional: []
    }
  }
};

/**
 * Subscription Management Endpoints
 * Manual subscription activation (Phase 1)
 */
const SUBSCRIPTION_ENDPOINTS = {
  GET_STATUS: {
    path: '/api/subscriptions/status',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get current subscription status',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    response: {
      success: ['isActive', 'expiryDate', 'renewalDate', 'plan']
    }
  },

  ACTIVATE: {
    path: '/api/subscriptions/activate',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Activate subscription (manual process in Phase 1)',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['planId']
    }
  },

  DEACTIVATE: {
    path: '/api/subscriptions/deactivate',
    method: 'POST',
    version: API_VERSIONS.V1,
    description: 'Deactivate subscription',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['reason']
    }
  },

  GET_HISTORY: {
    path: '/api/subscriptions/history',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get subscription history',
    requiresAuth: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['page', 'size', 'dateFrom', 'dateTo']
    }
  },

  GET_PLANS: {
    path: '/api/subscriptions/plans',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get available subscription plans',
    requiresAuth: false,
    parameters: {
      required: [],
      optional: ['currency']
    }
  }
};

/**
 * Surebet Data Endpoints
 * Following constitution requirements for MarketsAnalysisDTO
 */
const SUREBET_ENDPOINTS = {
  // Core surebet data (requires active subscription)
  MARKETS_ANALYSIS: {
    path: '/api/core/markets-analysis',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get profitable surebet opportunities (MarketsAnalysisDTO array)',
    requiresAuth: true,
    requiresSubscription: true,
    roles: ['User', 'Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: [
        'bookmakers', // comma-separated list
        'sports', // comma-separated list
        'championships', // comma-separated list
        'minMargin', // minimum profit margin
        'maxMargin', // maximum profit margin
        'dateFrom', // YYYY-MM-DD format
        'dateTo', // YYYY-MM-DD format
        'page', // pagination
        'size' // page size
      ]
    },
    response: {
      success: {
        structure: 'MarketsAnalysisDTO[]',
        fields: ['id', 'event', 'marginFlag', 'margins'],
        description: 'Array of surebet opportunities with dynamic margins object'
      }
    },
    caching: {
      enabled: true,
      ttl: 60000, // 1 minute
      key: 'markets_analysis'
    }
  },

  // Reference data endpoints
  GET_BOOKMAKERS: {
    path: '/api/core/bookmakers',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get list of supported bookmakers',
    requiresAuth: false,
    parameters: {
      required: [],
      optional: ['active', 'country']
    },
    caching: {
      enabled: true,
      ttl: 3600000, // 1 hour
      key: 'bookmakers'
    }
  },

  GET_SPORTS: {
    path: '/api/core/sports',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get list of supported sports',
    requiresAuth: false,
    parameters: {
      required: [],
      optional: ['active']
    },
    caching: {
      enabled: true,
      ttl: 3600000, // 1 hour
      key: 'sports'
    }
  },

  GET_CHAMPIONSHIPS: {
    path: '/api/core/championships',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get list of championships/leagues',
    requiresAuth: false,
    parameters: {
      required: [],
      optional: ['sport', 'country', 'active']
    },
    caching: {
      enabled: true,
      ttl: 1800000, // 30 minutes
      key: 'championships'
    }
  }
};

/**
 * Admin Endpoints (for future use)
 * Super Admin and Admin role access
 */
const ADMIN_ENDPOINTS = {
  GET_USERS: {
    path: '/api/admin/users',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get users list (admin only)',
    requiresAuth: true,
    roles: ['Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['page', 'size', 'search', 'role', 'status']
    }
  },

  GET_SUBSCRIPTIONS: {
    path: '/api/admin/subscriptions',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get subscriptions overview (admin only)',
    requiresAuth: true,
    roles: ['Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['page', 'size', 'status', 'dateFrom', 'dateTo']
    }
  },

  GET_ANALYTICS: {
    path: '/api/admin/analytics',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get platform analytics (admin only)',
    requiresAuth: true,
    roles: ['Admin', 'Super Admin'],
    parameters: {
      required: [],
      optional: ['period', 'metric', 'dateFrom', 'dateTo']
    }
  },

  GET_SYSTEM_STATUS: {
    path: '/api/admin/system-status',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get system health status (admin only)',
    requiresAuth: true,
    roles: ['Super Admin']
  }
};

/**
 * Health Check and Utility Endpoints
 */
const UTILITY_ENDPOINTS = {
  HEALTH_CHECK: {
    path: '/health',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'API health check endpoint',
    requiresAuth: false
  },

  VERSION_INFO: {
    path: '/api/version',
    method: 'GET',
    version: API_VERSIONS.V1,
    description: 'Get API version information',
    requiresAuth: false
  }
};

/**
 * Complete endpoint registry
 */
export const API_ENDPOINTS = {
  AUTH: AUTH_ENDPOINTS,
  USER: USER_ENDPOINTS,
  SUBSCRIPTION: SUBSCRIPTION_ENDPOINTS,
  SUREBET: SUREBET_ENDPOINTS,
  ADMIN: ADMIN_ENDPOINTS,
  UTILITY: UTILITY_ENDPOINTS
};

/**
 * URL Construction Utilities
 */
export class URLBuilder {
  constructor() {
    this.envConfig = getEnvironmentConfig();
  }

  /**
   * Build complete URL for an endpoint
   * @param {Object} endpoint - Endpoint configuration object
   * @param {Object} params - URL parameters for substitution
   * @param {Object} queryParams - Query parameters
   * @returns {string} Complete URL
   */
  buildURL(endpoint, params = {}, queryParams = {}) {
    if (!endpoint || !endpoint.path) {
      throw new Error('Invalid endpoint configuration');
    }

    // Start with base URL
    let url = this.envConfig.api.baseURL;

    // Add version if specified
    if (endpoint.version && endpoint.version !== API_VERSIONS.V1) {
      url += `/${endpoint.version}`;
    }

    // Add endpoint path with parameter substitution
    url += this.substituteParameters(endpoint.path, params);

    // Add query parameters
    const queryString = this.buildQueryString(queryParams);
    if (queryString) {
      url += `?${queryString}`;
    }

    return url;
  }

  /**
   * Substitute path parameters in URL
   * @param {string} path - URL path with parameters
   * @param {Object} params - Parameters to substitute
   * @returns {string} Path with substituted parameters
   */
  substituteParameters(path, params) {
    let substitutedPath = path;

    // Replace path parameters like /users/{userId}/profile
    Object.keys(params).forEach(key => {
      const placeholder = `{${key}}`;
      if (substitutedPath.includes(placeholder)) {
        substitutedPath = substitutedPath.replace(placeholder, encodeURIComponent(params[key]));
      }
    });

    // Check for unsubstituted parameters
    const remainingPlaceholders = substitutedPath.match(/\{[^}]+\}/g);
    if (remainingPlaceholders) {
      throw new Error(`Missing required parameters: ${remainingPlaceholders.join(', ')}`);
    }

    return substitutedPath;
  }

  /**
   * Build query string from parameters
   * @param {Object} params - Query parameters
   * @returns {string} Query string
   */
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
      return '';
    }

    const queryPairs = [];

    Object.keys(params).forEach(key => {
      const value = params[key];

      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          // Handle array parameters
          value.forEach(item => {
            queryPairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
          });
        } else {
          queryPairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      }
    });

    return queryPairs.join('&');
  }

  /**
   * Build URL for surebet filtering (specialized method)
   * @param {Object} filters - Surebet filter parameters
   * @returns {string} Complete URL with filters
   */
  buildSurebetURL(filters = {}) {
    const endpoint = API_ENDPOINTS.SUREBET.MARKETS_ANALYSIS;
    const queryParams = {};

    // Process filter parameters
    if (filters.bookmakers && filters.bookmakers.length > 0) {
      // Convert bookmaker IDs to API format (e.g., bg8888 -> bg_8888)
      if (Array.isArray(filters.bookmakers)) {
        const apiFormattedBookmakers = convertBookmakerIdsToApiFormat(filters.bookmakers);
        queryParams.bookmakers = apiFormattedBookmakers.join(',');
      } else {
        queryParams.bookmakers = convertBookmakersStringToApiFormat(filters.bookmakers);
      }
    }

    if (filters.sports && filters.sports.length > 0) {
      queryParams.sports = Array.isArray(filters.sports)
        ? filters.sports.join(',')
        : filters.sports;
    }

    if (filters.championships && filters.championships.length > 0) {
      queryParams.championships = Array.isArray(filters.championships)
        ? filters.championships.join(',')
        : filters.championships;
    }

    // Numeric filters
    if (filters.minMargin && filters.minMargin > 0) {
      queryParams.minMargin = filters.minMargin;
    }

    if (filters.maxMargin && filters.maxMargin > 0) {
      queryParams.maxMargin = filters.maxMargin;
    }

    // Date filters
    if (filters.dateFrom) {
      queryParams.dateFrom = filters.dateFrom;
    }

    if (filters.dateTo) {
      queryParams.dateTo = filters.dateTo;
    }

    // Pagination
    if (filters.page !== undefined) {
      queryParams.page = filters.page;
    }

    if (filters.size !== undefined) {
      queryParams.size = filters.size;
    }

    return this.buildURL(endpoint, {}, queryParams);
  }
}

/**
 * Parameter Validation Utilities
 */
export class ParameterValidator {
  /**
   * Validate endpoint parameters
   * @param {Object} endpoint - Endpoint configuration
   * @param {Object} params - Parameters to validate
   * @returns {Object} Validation result
   */
  static validateParameters(endpoint, params = {}) {
    const result = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (!endpoint || !endpoint.parameters) {
      return result;
    }

    // Check required parameters
    if (endpoint.parameters.required) {
      endpoint.parameters.required.forEach(requiredParam => {
        if (!(requiredParam in params) || params[requiredParam] === null || params[requiredParam] === undefined) {
          result.isValid = false;
          result.errors.push(`Missing required parameter: ${requiredParam}`);
        }
      });
    }

    // Check parameter types and formats
    Object.keys(params).forEach(paramName => {
      const value = params[paramName];

      // Validate email format
      if (paramName === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          result.isValid = false;
          result.errors.push(`Invalid email format: ${paramName}`);
        }
      }

      // Validate date format
      if ((paramName === 'dateFrom' || paramName === 'dateTo') && value) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(value)) {
          result.warnings.push(`Date parameter should be in YYYY-MM-DD format: ${paramName}`);
        }
      }

      // Validate pagination parameters
      if ((paramName === 'page' || paramName === 'size') && value !== undefined) {
        const numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue < 0) {
          result.isValid = false;
          result.errors.push(`Invalid pagination parameter: ${paramName} must be a non-negative integer`);
        }
      }
    });

    return result;
  }

  /**
   * Validate surebet filter parameters
   * @param {Object} filters - Filter parameters
   * @returns {Object} Validation result
   */
  static validateSurebetFilters(filters = {}) {
    const result = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Validate margin parameters
    if (filters.minMargin !== undefined) {
      const minMargin = Number(filters.minMargin);
      if (isNaN(minMargin) || minMargin < 0) {
        result.isValid = false;
        result.errors.push('minMargin must be a non-negative number');
      }
    }

    if (filters.maxMargin !== undefined) {
      const maxMargin = Number(filters.maxMargin);
      if (isNaN(maxMargin) || maxMargin < 0) {
        result.isValid = false;
        result.errors.push('maxMargin must be a non-negative number');
      }
    }

    // Check margin range
    if (filters.minMargin !== undefined && filters.maxMargin !== undefined) {
      if (Number(filters.minMargin) > Number(filters.maxMargin)) {
        result.isValid = false;
        result.errors.push('minMargin cannot be greater than maxMargin');
      }
    }

    // Validate array parameters
    ['bookmakers', 'sports', 'championships'].forEach(arrayParam => {
      if (filters[arrayParam] && !Array.isArray(filters[arrayParam]) && typeof filters[arrayParam] !== 'string') {
        result.warnings.push(`${arrayParam} should be an array or comma-separated string`);
      }
    });

    return result;
  }
}

/**
 * Endpoint Testing Utilities
 */
export class EndpointTester {
  constructor() {
    this.urlBuilder = new URLBuilder();
  }

  /**
   * Test endpoint URL construction
   * @param {string} endpointPath - Path to endpoint (e.g., 'AUTH.LOGIN')
   * @param {Object} params - URL parameters
   * @param {Object} queryParams - Query parameters
   * @returns {Object} Test result
   */
  testEndpoint(endpointPath, params = {}, queryParams = {}) {
    try {
      const endpoint = this.getEndpointByPath(endpointPath);
      if (!endpoint) {
        return {
          success: false,
          error: `Endpoint not found: ${endpointPath}`
        };
      }

      // Validate parameters
      const validation = ParameterValidator.validateParameters(endpoint, { ...params, ...queryParams });

      // Build URL
      const url = this.urlBuilder.buildURL(endpoint, params, queryParams);

      return {
        success: true,
        endpoint: endpoint,
        url: url,
        validation: validation,
        method: endpoint.method,
        requiresAuth: endpoint.requiresAuth,
        requiresSubscription: endpoint.requiresSubscription
      };

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get endpoint configuration by path
   * @param {string} path - Dot notation path (e.g., 'AUTH.LOGIN')
   * @returns {Object|null} Endpoint configuration
   */
  getEndpointByPath(path) {
    const pathParts = path.split('.');
    let current = API_ENDPOINTS;

    for (const part of pathParts) {
      if (current[part]) {
        current = current[part];
      } else {
        return null;
      }
    }

    return current;
  }

  /**
   * Test all endpoints for basic configuration validity
   * @returns {Object} Test results summary
   */
  testAllEndpoints() {
    const results = {
      total: 0,
      passed: 0,
      failed: 0,
      errors: []
    };

    const testEndpointGroup = (group, groupName) => {
      Object.keys(group).forEach(endpointName => {
        const endpoint = group[endpointName];
        results.total++;

        try {
          // Basic validation
          if (!endpoint.path || !endpoint.method) {
            results.failed++;
            results.errors.push(`${groupName}.${endpointName}: Missing path or method`);
            return;
          }

          // Test URL construction with empty parameters
          this.urlBuilder.buildURL(endpoint, {}, {});
          results.passed++;

        } catch (error) {
          results.failed++;
          results.errors.push(`${groupName}.${endpointName}: ${error.message}`);
        }
      });
    };

    // Test all endpoint groups
    Object.keys(API_ENDPOINTS).forEach(groupName => {
      testEndpointGroup(API_ENDPOINTS[groupName], groupName);
    });

    return results;
  }
}

/**
 * Create URL builder instance
 */
export const urlBuilder = new URLBuilder();

/**
 * Export endpoint utilities
 */
export const endpointUtils = {
  build: (endpoint, params, queryParams) => urlBuilder.buildURL(endpoint, params, queryParams),
  buildSurebet: (filters) => urlBuilder.buildSurebetURL(filters),
  validate: (endpoint, params) => ParameterValidator.validateParameters(endpoint, params),
  validateFilters: (filters) => ParameterValidator.validateSurebetFilters(filters),
  test: (endpointPath, params, queryParams) => new EndpointTester().testEndpoint(endpointPath, params, queryParams)
};

/**
 * Export API versions and configuration
 */
export { API_VERSIONS };

export default {
  API_ENDPOINTS,
  URLBuilder,
  ParameterValidator,
  EndpointTester,
  urlBuilder,
  endpointUtils,
  API_VERSIONS
};
